import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
  useDeferredValue,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { DropdownMenu } from 'radix-ui'
import { LogOut } from 'lucide-react'
import { ScopeSwitcher } from '@nyos/core-framework'
import {
  useYardSearch,
  parseSearchQuery,
  filterYard,
  flattenSearchResults,
  recordSearchRecent,
  loadSearchRecents,
  MIN_QUERY_LENGTH,
  SearchHit,
  SearchScope,
  useAuth,
} from '@nyos/shared'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useYard } from '../contexts/YardContext'
import { useWebSocketContext } from '../contexts/WebSocketContext'
import SearchResultsPanel, { searchOptionId } from './SearchResultsPanel'

export interface GlobalHeaderRef {
  notificationIconRef: React.RefObject<HTMLButtonElement | null>
  searchInputRef: React.RefObject<HTMLInputElement | null>
}

interface GlobalHeaderProps {
  onNotificationsClick: () => void
  onHealthClick: () => void
  /** Landing hook — App routes the hit to map focus / inspector / queue. */
  onSearchSelect?: (hit: SearchHit) => void
}

const LISTBOX_ID = 'yard-search-listbox'

/**
 * Returns true when any drawer or modal is mounted. Our drawers render
 * nothing when closed, so the presence of [data-drawer] in the DOM is a
 * reliable "an overlay is open" signal for the '/' focus guard.
 */
const isOverlayOpen = () =>
  typeof document !== 'undefined' &&
  document.querySelector('[data-drawer], [role="dialog"]') !== null

const GlobalHeader = forwardRef<GlobalHeaderRef, GlobalHeaderProps>(({
  onNotificationsClick,
  onHealthClick,
  onSearchSelect,
}, ref) => {
  const notificationIconRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { activeFacilityId } = useYard()
  const { subscribe } = useWebSocketContext()
  const queryClient = useQueryClient()
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  // Sign-out: discard session state + cached server data, then land on the
  // login route. Stateless JWT — no server call (revocation is post-demo).
  const handleSignOut = () => {
    logout()
    queryClient.clear()
    navigate('/login', { replace: true })
  }

  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [chipScope, setChipScope] = useState<SearchScope>('all')
  const [activeIndex, setActiveIndex] = useState(-1)
  const [recents, setRecents] = useState<string[]>([])

  const { data, isLoading, queryKeys } = useYardSearch(activeFacilityId)

  // useDeferredValue is acceptable ONLY because filtering is client-side
  // over the cached snapshot — zero network per keystroke. If search ever
  // hits the server, switch to a real debounce (shared/utils/debounce).
  const deferredQuery = useDeferredValue(query)
  const results = useMemo(() => {
    const parsed = parseSearchQuery(deferredQuery)
    // Explicit `car:`/`trk:`/`job:` prefix wins over the chips; marks
    // auto-scope applies when the chips are on 'all'.
    const scope = parsed.explicit ? parsed.scope : chipScope !== 'all' ? chipScope : parsed.scope
    return filterYard(data, { ...parsed, scope })
  }, [deferredQuery, chipScope, data])

  const flat = useMemo(() => flattenSearchResults(results), [results])

  // Reset highlight when the result set changes.
  useEffect(() => {
    setActiveIndex(flat.length > 0 ? 0 : -1)
  }, [flat])

  // Keep the cached snapshot fresh off the live WS bus — invalidate on
  // structural events only (not per-car position snaps, which would churn
  // the query every frame).
  useEffect(() => {
    return subscribe((message: any) => {
      const type = String(message?.type ?? message?.eventType ?? '')
      if (/WORK|SWITCH|TRACK|ASSET|YARD|INTERCHANGE/.test(type)) {
        queryClient.invalidateQueries({ queryKey: queryKeys.cars })
        queryClient.invalidateQueries({ queryKey: queryKeys.jobs })
        queryClient.invalidateQueries({ queryKey: queryKeys.tracks })
      }
    })
  }, [subscribe, queryClient, queryKeys])

  // '/' focuses the input — ignored while typing elsewhere or while a
  // drawer/modal is open (data-drawer/role=dialog guard — see step commit).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      const el = document.activeElement as HTMLElement | null
      const tag = el?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return
      if (isOverlayOpen()) return
      e.preventDefault()
      setRecents(loadSearchRecents(activeFacilityId))
      setOpen(true)
      searchInputRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeFacilityId])

  // Click-outside closes the panel (focus stays wherever it lands).
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        // Touch devices don't always move focus on tap-outside — blur so
        // the soft keyboard drops. (TODO(tablet-refactor): same extraction
        // target as handleSelect — shared dismissSoftKeyboard util.)
        searchInputRef.current?.blur()
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  useImperativeHandle(ref, () => ({
    notificationIconRef,
    searchInputRef,
  }))

  const handleSelect = useCallback(
    (hit: SearchHit) => {
      setRecents(recordSearchRecent(activeFacilityId, query))
      setOpen(false)
      setQuery('')
      // Blur so mobile soft keyboards dismiss on selection — otherwise the
      // keyboard stays up over the destination screen until a manual tap.
      // TODO(tablet-refactor): blur-on-dismiss is a soft-keyboard pattern
      // every touch search/combobox needs — extract to
      // `shared/src/utils/keyboard.ts` `dismissSoftKeyboard(el)` (or a
      // useKeyboardDismiss hook) when the mobile target adds search.
      searchInputRef.current?.blur()
      onSearchSelect?.(hit)
    },
    [activeFacilityId, onSearchSelect, query]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown') {
        setRecents(loadSearchRecents(activeFacilityId))
        setOpen(true)
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((i) => (flat.length ? (i + 1) % flat.length : -1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((i) => (flat.length ? (i - 1 + flat.length) % flat.length : -1))
        break
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < flat.length) {
          e.preventDefault()
          handleSelect(flat[activeIndex])
        }
        break
      case 'Escape':
        // stopPropagation: App's global Escape → close-drawers listener
        // must not fire — Esc here only closes the panel. Focus stays on
        // the input (that's the "focus return" — it never left).
        e.stopPropagation()
        setOpen(false)
        break
    }
  }

  const showResults = query.trim().length >= MIN_QUERY_LENGTH

  return (
    <header className="h-16 bg-bg-header border-b border-border flex items-center px-6 gap-6">
      <div className="flex items-center gap-4 flex-1">
        <div className="font-bold text-sm text-accent-blue whitespace-nowrap">Neuralogyx.ai</div>
        <div ref={containerRef} className="flex-1 max-w-[280px] min-[1100px]:max-w-[400px] relative flex items-center">
          <svg
            className="absolute left-3 z-1 text-text-muted pointer-events-none"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <Input
            ref={searchInputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={LISTBOX_ID}
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? searchOptionId(activeIndex) : undefined}
            aria-label="Search tracks, railcars, switch lists"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
            onFocus={() => {
              setRecents(loadSearchRecents(activeFacilityId))
              setOpen(true)
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search tracks, railcars, work orders..."
            className="pl-9"
          />
          {open && (
            <SearchResultsPanel
              query={query}
              results={results}
              recents={recents}
              scope={chipScope}
              onScopeChange={setChipScope}
              activeIndex={activeIndex}
              onHoverIndex={setActiveIndex}
              onSelect={handleSelect}
              onRecentClick={(term) => setQuery(term)}
              isLoading={isLoading && showResults}
              listboxId={LISTBOX_ID}
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-1 justify-end">
        <ScopeSwitcher />
        <div className="flex items-center gap-2 text-sm text-text-muted">
          <div className="w-2 h-2 rounded-full bg-switch-normal animate-pulse"></div>
          <span className="daylight:text-[#1A1D23]">ONLINE (Simulating)</span>
        </div>
        <Button
          ref={notificationIconRef}
          variant="ghost"
          size="icon"
          onClick={onNotificationsClick}
          title="Notifications"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onHealthClick}
          title="System Health"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
          </svg>
        </Button>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="ghost" size="icon" title="Profile">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="min-w-56 bg-bg-drawer border border-border rounded-md shadow-lg p-1 z-50"
            >
              <DropdownMenu.Label className="px-3 py-2">
                <div className="text-sm font-medium text-text-primary">{user?.username}</div>
                <div className="text-xs text-text-muted">{user?.role?.replace(/^ROLE_/, '')}</div>
              </DropdownMenu.Label>
              <DropdownMenu.Separator className="h-px bg-border my-1" />
              <DropdownMenu.Item
                onSelect={handleSignOut}
                className="flex items-center gap-2 px-3 py-1.5 text-sm outline-none cursor-pointer rounded-sm text-status-red data-[highlighted]:bg-hover"
              >
                <LogOut size={14} />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  )
})

GlobalHeader.displayName = 'GlobalHeader'

export default React.memo(GlobalHeader)
